import { useState } from "react";
import { subscribeNewsletter } from "@/lib/api";
import { useT } from "@/context/LanguageContext";

/**
 * NewsletterBlock — subscription banner rendered as its own section above
 * the Footer. Uses the cream storefront palette (not the dark footer palette)
 * so it reads as part of the shopping experience rather than legal text.
 */
const NewsletterBlock = () => {
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | already | error
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("loading");
    setError(null);
    try {
      const res = await subscribeNewsletter(email.trim(), "footer");
      setState(res?.already_subscribed ? "already" : "done");
      setEmail("");
    } catch (err) {
      setError(err?.response?.data?.detail || t("footer.newsletter.error"));
      setState("error");
    }
  };

  return (
    <section
      data-testid="newsletter-block"
      className="border-t border-b border-[#F1DCC5] bg-[#FBF6EE]"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="font-typewriter text-[10px] tracking-[0.32em] uppercase text-[var(--gl-orange-hover)] mb-3">
            — Newsletter —
          </div>
          <h3 className="font-serif-display text-3xl md:text-4xl text-[#241C18] leading-tight">
            {t("footer.newsletter.title")}
          </h3>
          <p className="mt-3 text-sm text-[#6B6862] max-w-md leading-relaxed">
            {t("footer.newsletter.sub")}
          </p>
        </div>
        <form
          onSubmit={submit}
          data-testid="newsletter-form"
          className="flex flex-col sm:flex-row gap-2 w-full"
        >
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("footer.newsletter.placeholder")}
            data-testid="newsletter-email-input"
            className="flex-1 px-5 py-3 rounded-full bg-white border border-[#F1DCC5] text-[#241C18] placeholder-[#8A7568] focus:outline-none focus:border-[var(--gl-orange)]"
          />
          <button
            type="submit"
            disabled={state === "loading"}
            data-testid="newsletter-submit-btn"
            className="px-6 py-3 rounded-full bg-[#241C18] text-[var(--gl-orange)] font-badge text-sm tracking-[0.14em] uppercase hover:bg-black disabled:opacity-60 transition"
          >
            {state === "loading" ? "…" : t("footer.subscribe")}
          </button>
        </form>
      </div>
      {(state === "done" || state === "already") && (
        <div
          data-testid="newsletter-success"
          className="max-w-6xl mx-auto px-6 md:px-12 pb-6 text-sm text-[var(--gl-orange-hover)] font-typewriter"
        >
          {state === "already" ? t("footer.newsletter.already") : t("footer.newsletter.success")}
        </div>
      )}
      {state === "error" && error && (
        <div className="max-w-6xl mx-auto px-6 md:px-12 pb-6 text-sm text-[#C97A63] font-typewriter">
          {error}
        </div>
      )}
    </section>
  );
};

export default NewsletterBlock;
